import { Component, computed, EventEmitter, inject, Input, Output } from "@angular/core";

import { config } from "@config";
import { MasterdataService } from "@core-services/masterdata.service";
import { TranslationService } from "@core-services/translation.service";
import { currentDateWithoutTime } from "@helpers/date";
import { formatIban } from "@helpers/utils";
import { FiatService } from "@main-services/currency";
import { PayAccountService } from "@main-services/pay_account.service";
import { UserService } from "@main-services/user.service";
import { Fiat, Field, PayoutAccount, Provider, User, Validateable } from "@models";
import { BpModalHasSave } from "@models/interfaces/bp-modal-has-save";
import { FiatTransaction } from "@models/transaction";
import { VerifyPayeeMode, VerifyPayeeResult } from "@models/verify-payee";
import { SummaryComponent } from "../../summary/summary";
import { VerifyPayeeService } from "../services/verify-payee.service";

@Component({
	selector: "fiat-withdraw-summary",
	templateUrl: "./fiat-withdraw-summary.component.html",
	styleUrls: ["./fiat-withdraw-summary.component.scss"],
	standalone: false
})
export class FiatWithdrawSummaryComponent extends SummaryComponent implements BpModalHasSave {
	@Input() public offer: FiatTransaction = null;

	@Output()
	public payeeAction: EventEmitter<void> = new EventEmitter();

	private readonly verifyPayeeService = inject(VerifyPayeeService);

	public summarySwitch: SummarySwitch = new SummarySwitch();
	public showLoadingSpinner = this.verifyPayeeService.showLoadingSpinner;
	public verifyPayeeAccountHolder = computed(
		() => this.verifyPayeeService.verifyPayeeResponse()?.result_reported_account_holder
	);
	public isVopFlagEnabled = this.verifyPayeeService.isVopFlagEnabled();
	public isBankTransfer = this.verifyPayeeService.isBankTransfer();

	private verifyPayeeResult = computed(() => this.verifyPayeeService.verifyPayeeResponse()?.result);
	private verifyPayeeMode = this.verifyPayeeService.verifyPayeeMode;
	public sendEnabled = computed(() => this.verifyPayeeMode() === VerifyPayeeMode.SEND);

	private payeeResultMapping = new Map<VerifyPayeeResult, string>([
		[VerifyPayeeResult.FULL_MATCH, "full"],
		[VerifyPayeeResult.CLOSE_MATCH, "partial"],
		[VerifyPayeeResult.NO_MATCH, this.sendEnabled() ? "partial" : "no"],
		[VerifyPayeeResult.NOT_APPLICABLE, "notApplicable"],
		[VerifyPayeeResult.ERROR, "error"]
	]);

	public isTermsSwitchDisabled = computed(
		() =>
			this.verifyPayeeResult() === VerifyPayeeResult.NO_MATCH &&
			this.verifyPayeeMode() === VerifyPayeeMode.WITHDRAWAL
	);

	public mappedPayeeResult = computed(() => {
		const isSendMode: boolean = this.sendEnabled();
		let title: string;
		let description: string;
		let linkAction: string;
		const terms: string = isSendMode ? "send_summary_vop_terms" : "withdraw_summary_vop_terms";
		switch (this.verifyPayeeResult()) {
			case VerifyPayeeResult.FULL_MATCH:
				title = isSendMode ? "send_summary_vop_fullmatch_title" : "withdraw_summary_vop_fullmatch_title";
				description = isSendMode
					? "send_summary_vop_fullmatch_subtitle"
					: "withdraw_summary_vop_fullmatch_subtitle";
				break;
			case VerifyPayeeResult.CLOSE_MATCH:
				title = "send_summary_vop_partialmatch_title";
				description = isSendMode
					? "send_summary_vop_partialmatch_subtitle"
					: "withdraw_summary_vop_partialmatch_subtitle";
				linkAction = isSendMode
					? "send_summary_vop_partialmatch_link"
					: "withdraw_summary_vop_partialmatch_link";
				break;
			case VerifyPayeeResult.NO_MATCH:
				title = isSendMode ? "send_summary_vop_nomatch_title" : "withdraw_summary_vop_nomatch_title";
				description = isSendMode
					? "send_summary_vop_nomatch_subtitle"
					: "withdraw_summary_vop_nomatch_subtitle";
				linkAction = isSendMode ? "send_summary_vop_nomatch_link" : "withdraw_summary_vop_nomatch_link";
				break;
			case VerifyPayeeResult.NOT_APPLICABLE:
				title = isSendMode
					? "send_summary_vop_notapplicable_title"
					: "withdraw_summary_vop_notapplicable_title";
				description = isSendMode
					? "send_summary_vop_notapplicable_subtitle"
					: "withdraw_summary_vop_notapplicable_subtitle";
				break;
			case VerifyPayeeResult.ERROR:
				title = isSendMode ? "send_summary_vop_noavailable_title" : "withdraw_summary_vop_noavailable_title";
				description = isSendMode
					? "send_summary_vop_noavailable_subtitle"
					: "withdraw_summary_vop_noavailable_subtitle";
				linkAction = isSendMode ? "send_summary_vop_noavailable_link" : "withdraw_summary_vop_noavailable_link";
				break;
			default:
				title = "";
				description = "";
				linkAction = "";
				break;
		}
		return { class: this.payeeResultMapping.get(this.verifyPayeeResult()), title, description, linkAction, terms };
	});

	public constructor(
		private masterDataService: MasterdataService,
		private fiatService: FiatService,
		private userService: UserService,
		private accountService: PayAccountService,
		private translationService: TranslationService
	) {
		super();
	}

	public save(twofa: string = null): void {
		if (twofa != null) {
			this.offer._auth_code = twofa;
		}

		this.finish.emit(this.offer);
	}

	public close(): void {
		this.onClose.emit();
	}

	public get provider(): Provider {
		return this.masterDataService.getPayoutProviderById(this.payoutAccount.getProviderId());
	}

	public get fiat(): Fiat {
		return this.fiatService.getItemById(this.offer.getFiatId());
	}

	public getLogoProvider(): string {
		return this.masterDataService.logoUrl(this.payoutAccount);
	}

	public getProviderShortName(): string {
		return this.provider.getShortName();
	}

	public isProviderAmazon(): boolean {
		return this.provider.isShortNameAmazon();
	}

	public get payoutAccount(): PayoutAccount {
		return this.accountService.getItemById(this.offer.getPayoutAccountId());
	}

	public get fields(): Field[] {
		const fields = [];
		this.provider
			.getFields()
			.filter((f) => f.isMandatory())
			.forEach((f) => {
				const payoutfield = this.payoutAccount.getFields().find((pf) => pf.getName() === f.getName());
				if (payoutfield) {
					fields.push(payoutfield);
				}
			});
		return fields;
	}

	public get type(): string {
		return this.offer.getUserWantsType();
	}

	public get needs2fa(): boolean {
		return this.user.get2FA();
	}

	public get user(): User {
		return this.userService.user;
	}

	public setEdit(e: number): void {
		if (this.accepted) {
			return;
		}
		this.edit.emit(e);
	}

	public formatIban(iban: string): string {
		return formatIban(iban);
	}

	public get disabledConfirmButton(): boolean {
		if (this.isVopFlagEnabled) {
			return (
				((this.sendEnabled() || this.isBankTransfer) && !this.summarySwitch.user_confirm) ||
				this.isTermsSwitchDisabled()
			);
		}
		return this.sendEnabled() && !(this.summarySwitch.term_policy && this.summarySwitch.user_confirm);
	}

	public getPaymentsTermsLink(): string {
		return (config.baseUrl + config.externalPaths["payments"]["termsLink"]).replace(
			"%s",
			TranslationService.getLanguageIsoCode()
		);
	}

	public getPaymentsPolicyLink(): string {
		return (config.baseUrl + config.externalPaths["payments"]["policyLink"]).replace(
			"%s",
			TranslationService.getLanguageIsoCode()
		);
	}

	public getCurrentDate(): string {
		return currentDateWithoutTime();
	}

	public onConfirmButtonClick(): void {
		// Check for wiggle
		const userHasNotConfirmed = this.isVopFlagEnabled
			? !this.summarySwitch.user_confirm
			: !this.summarySwitch.term_policy || !this.summarySwitch.user_confirm;
		if (this.sendEnabled() && userHasNotConfirmed) {
			this.summarySwitch.wiggle("user_confirm");
		}

		if (!this.needs2fa) {
			this.save();
		} else if (this.needs2fa && !this.accepted) {
			this.accepted = true;
		}
	}

	public get headerTitle(): string {
		if (this.needs2fa && this.accepted) {
			return this.translationService.translate("confirm_transaction");
		}
		return (
			this.translationService.translate(this.sendEnabled() ? "send_summary_title" : "withdraw") +
			" " +
			this.fiat.getName()
		);
	}
}

class SummarySwitch extends Validateable {
	public term_policy?: boolean = false;
	public user_confirm: boolean = false;
}
